

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewReferralRedemptionModal from 'components/NewReferralRedemptionModal';

const ReferralRedemptions = () => {
    const [redemptions, setRedemptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('referral_redemptions');

    useEffect(() => {
        fetchRedemptions();
    }, []);

    const fetchRedemptions = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/referral-redemptions/');
            setRedemptions(response.data);
        } catch (error) {
            console.error('Error fetching referral redemptions:', error);
            setSnackbar({ open: true, message: 'Error loading referral redemptions', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveRedemption = async (payload) => {
        try {
            await axiosInstance.post('/referral-redemptions/', payload);
            setSnackbar({ open: true, message: 'Referral Redemption created successfully', severity: 'success' });
            fetchRedemptions();
        } catch (error) {
            console.error('Error saving referral redemption:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/referral-redemption/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "20%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        { Header: "Tier Code", accessor: "tier_code", width: "12%" },
        { Header: "Tier Rank", accessor: "tier_rank", width: "10%" },
        { Header: "Status", accessor: "status", width: "12%" },
        { Header: "Qualify Metric", accessor: "qualify_metric", width: "12%" },
        { Header: "Qualify Threshold", accessor: "qualify_threshold", width: "12%" },
        {
            Header: "Earn Multiplier",
            accessor: "earn_multiplier",
            width: "12%",
            Cell: ({ value }) => value ? parseFloat(value).toFixed(2) : '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "10%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: redemptions,
    }), [redemptions]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Referral Redemptions</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Redemption
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : redemptions.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Referral Redemption" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewReferralRedemptionModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveRedemption}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default ReferralRedemptions;

